import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper } from '@mui/material';
//import { useLocation } from 'react-router-dom';

import { ChatMessage, VersionData } from '../../IdeationService';
import Loader from '../../../../common/components/Loader';

// ── Child components ───────────────────────────────────────────────────────────
import ChatHeader from './components/ChatHeader';
import UserMessage from './components/UserMessage';
import BotMessage, { TypingIndicator } from './components/BotMessage';
import MessageInput from './components/MessageInput';

// ── Types ──────────────────────────────────────────────────────────────────────
interface CoiChatProps {
  width?: number;
  onWidthChange?: (width: number) => void;
  onVersionChange?: (version: string, fileSummary: string) => void;
  currentVersion?: string;
  handleSendMessage: (input: string, isFirst?: boolean, id?: string) => void;
  messages: ChatMessage[];
  setMessages: (msgs: ChatMessage[]) => void;
  isSending: boolean;
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  useCaseDetails: Record<string, any>;
  versionData: VersionData;
  handleBack: () => void;
  field: string;
  setContextState: (state: any) => void;
  contextState: any;
  setChatId: (id: string) => void;
  initialChatId?: string;
  chatId?: string;
  setuseCaseDetails: (details: Record<string, any>) => void;
  onRefetchSessions?: () => void;
  handleNewChat: () => void;
  setUploadingImage?: (val: boolean) => void;
  conversationType?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Returns true when a user-facing bubble should be rendered for this message */
const hasUserContent = (msg: ChatMessage): boolean =>
  !!msg?.query?.user_query ||
  (Array.isArray(msg?.files?.data) && msg.files.data.length > 0) ||
  (Array.isArray(msg?.context_knowledge?.data) && msg.context_knowledge.data.length > 0);

/** Returns true when an assistant bubble should be rendered for this message */
const hasBotContent = (msg: ChatMessage): boolean =>
  (typeof msg.answer === 'string' && msg.answer.trim() !== '') ||
  !!(msg?.discoveryContent?.file_summary && msg?.version);

// ── Component ──────────────────────────────────────────────────────────────────

const CoiChat = ({
  width = 600,
  onWidthChange,
  onVersionChange,
  handleSendMessage,
  messages,
  isSending,
  inputMessage = '',
  setInputMessage,
  useCaseDetails,
  field = '',
  setContextState,
  contextState,
  versionData,
  chatId,
  setuseCaseDetails,
  onRefetchSessions,
  handleNewChat,
  setUploadingImage = () => {},
  conversationType = '',
}: CoiChatProps) => {
  const [isLoading] = useState(false);
  const [filePreview, setFilePreview] = useState<{ url: string; fileName?: string } | null>(null);
  const [isResizingState, setIsResizingState] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  // ── Scroll to bottom on new messages ──────────────────────────
  useEffect(() => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 100);
    });
  }, [messages]);

  // Initial greeting message removed to prevent redundant session creation.
  // Content from landing page is handled via useChatMessaging getPreviousChat and isNewChat state.

  // ── Resize handle ──────────────────────────────────────────────
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;
      const containerLeft = containerRef.current.getBoundingClientRect().left;
      const newWidth = e.clientX - containerLeft;
      if (newWidth >= 400 && newWidth <= 800) onWidthChange?.(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      setIsResizingState(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onWidthChange]);

  const handleMouseDown = () => {
    isResizing.current = true;
    setIsResizingState(true);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  // ── Version selection ──────────────────────────────────────────
  const handleVersionSelect = (version: string, fileSummary = '') =>
    onVersionChange?.(version, fileSummary);

  // ── Loading state ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <Box
        sx={{
          width,
          height: 'calc(100vh - 100px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className={isResizingState ? 'resizing' : ''}
      sx={{
        width,
        mx: { xs: 0, md: 1 },
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover .resize-handle': {
          opacity: 1,
        },
        '&.resizing .resize-handle': {
          opacity: 1,
        },
      }}
    >
      {/* ── Header ── */}
      <ChatHeader
        title={useCaseDetails?.title || 'AI Opportunity Discovery'}
        chatId={chatId || ''}
        field={field}
        useCaseDetails={useCaseDetails}
        setuseCaseDetails={setuseCaseDetails}
        onRefetchSessions={onRefetchSessions}
        handleNewChat={handleNewChat}
        conversationType={conversationType}
      />

      {/* ── Messages ── */}
      <Paper
        ref={messagesContainerRef}
        elevation={0}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
          px: 1,
          backgroundColor: '#fff',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { width: '6px', height: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '3px' },
        }}
      >
        {(messages || []).map((message, index) => (
          <React.Fragment key={message.id || index}>
            {hasUserContent(message) && (
              <UserMessage
                message={message}
                onFileClick={(url, fileName) => setFilePreview({ url, fileName })}
              />
            )}
            {hasBotContent(message) && (
              <BotMessage
                message={message}
                sessionTitle={useCaseDetails?.title}
                onVersionSelect={handleVersionSelect}
              />
            )}
          </React.Fragment>
        ))}

        {isSending && <TypingIndicator />}

        {/* Scroll anchor */}
        <div />
      </Paper>

      {/* ── Input ── */}
      <MessageInput
        inputMessage={inputMessage}
        setInputMessage={setInputMessage}
        isSending={isSending}
        contextState={contextState}
        setContextState={setContextState}
        versionData={versionData}
        filePreview={filePreview}
        setFilePreview={setFilePreview}
        setUploadingImage={setUploadingImage}
        onSend={handleSendMessage}
      />

      {/* ── Resize handle ── */}
      <Box
        ref={resizeRef}
        className="resize-handle"
        onMouseDown={handleMouseDown}
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 3,
          height: 21,
          borderRadius: 1,
          backgroundColor: '#B7B7B7',
          cursor: 'ew-resize',
          zIndex: 10,
          opacity: 0,
          transition: 'opacity 0.2s, background-color 0.2s, height 0.2s',

          '&:hover': {
            backgroundColor: 'primary.main',
          },
        }}
      />
    </Box>
  );
};

export default CoiChat;
